import type { Id } from "@convex/_generated/dataModel";

export interface ReasonTracking {
  reason: string;
  amount: string;
  permissionCode: {
    employeeId: string;
    employeeName: string;
  };
}

export interface SalesSummary {
  categoryId: string;
  categoryName: string;
  amount: string;
  quantity: number;
}

export interface TransactionSummary {
  cashCollected: string;
  cashSales: string;
  cashDeposits: string;
  cashWithdrawals: string;
  difference: string;
  inputByEmployeeId: string;
}

export interface BusinessReportSettings {
  showOrdersAndBillsSummary: boolean;
  showSalesCategoriesSummary: boolean;
  showSalesAndTaxesSummary: boolean;
  showDevicesSummary: boolean;
  showVoidedItems: boolean;
  showDiscountedItems: boolean;
  showRefundedItems: boolean;
  showManagerCodes: boolean;
  reprintBusinessReportAtEndOfDay: boolean;
  showDividers: boolean;
  printerDeviceId: "all" | Id<"printers">;
  printAutomaticallyAtEndOfDay: boolean;
  printCopies: number;
}

export function isBusinessReportSettings(
  value: unknown
): value is BusinessReportSettings {
  return (
    typeof value === "object" &&
    value !== null &&
    "showSalesAndTaxesSummary" in value
  );
}
export interface BusinessReportDataFromBackend {
  totalSales: number;
  totalTaxes: number;
  ordersCreated?: number;
  billsClosed?: number;
  salesByCategory?: SalesSummary[];
  deviceTransactions?: TransactionSummary | TransactionSummary[];
  voidedItems?: ReasonTracking[];
  discountedItems?: ReasonTracking[];
  refundedItems?: ReasonTracking[];
}

// Type for display data (with string values for formatting)
export interface BusinessReportData {
  totalSales: string;
  totalTaxes: string;
  ordersCreated: number;
  billsClosed: number;
  salesByCategory: SalesSummary[];
  deviceTransactions: TransactionSummary | TransactionSummary[];
  voidedItems: ReasonTracking[];
  discountedItems: ReasonTracking[];
  refundedItems: ReasonTracking[];
}

// Helper interface for visibility conditions
export interface VisibilitySettings {
  showOrdersAndBillsSummary: boolean;
  showSalesCategoriesSummary: boolean;
  showSalesAndTaxesSummary: boolean;
  showDevicesSummary: boolean;
  showVoidedItems: boolean;
  showDiscountedItems: boolean;
  showRefundedItems: boolean;
}

// Helper to calculate total amount from ReasonTracking items
export function calculateItemsTotal(items: ReasonTracking[]): string {
  return items.reduce((sum, item) => sum + Number(item.amount), 0).toFixed(2);
}

// Helper to check if report has real data
export function checkReportHasRealData(
  report: BusinessReportDataFromBackend | null | undefined
): boolean {
  if (!report) {
    return false;
  }

  const hasSalesOrTaxes = report.totalSales > 0 || report.totalTaxes > 0;
  const hasOrders =
    (report.ordersCreated ?? 0) > 0 || (report.billsClosed ?? 0) > 0;
  const hasSalesCategories = Boolean(report.salesByCategory?.length);
  const hasDeviceTransactions = Boolean(
    report.deviceTransactions &&
    (Array.isArray(report.deviceTransactions)
      ? report.deviceTransactions.length > 0
      : true)
  );
  const hasVoidedItems = Boolean(report.voidedItems?.length);
  const hasDiscountedItems = Boolean(report.discountedItems?.length);
  const hasRefundedItems = Boolean(report.refundedItems?.length);

  return (
    hasSalesOrTaxes ||
    hasOrders ||
    hasSalesCategories ||
    hasDeviceTransactions ||
    hasVoidedItems ||
    hasDiscountedItems ||
    hasRefundedItems
  );
}

// Helper to format backend report data to display data
export function formatReportForDisplay(
  report: BusinessReportDataFromBackend
): BusinessReportData {
  return {
    totalSales: report.totalSales.toFixed(2),
    totalTaxes: report.totalTaxes.toFixed(2),
    ordersCreated: report.ordersCreated ?? 0,
    billsClosed: report.billsClosed ?? 0,
    salesByCategory: report.salesByCategory ?? [],
    deviceTransactions: report.deviceTransactions ?? [],
    voidedItems: report.voidedItems ?? [],
    discountedItems: report.discountedItems ?? [],
    refundedItems: report.refundedItems ?? [],
  };
}

// Helper to compute visibility conditions
export function computeVisibility(
  settings: VisibilitySettings,
  isPreviewData: boolean,
  displayData: BusinessReportData
) {
  return {
    shouldShowOrdersBills:
      settings.showOrdersAndBillsSummary &&
      (isPreviewData || displayData.ordersCreated || displayData.billsClosed),
    shouldShowSalesCategories:
      settings.showSalesCategoriesSummary &&
      (isPreviewData || displayData.salesByCategory),
    shouldShowSalesTaxes:
      settings.showSalesAndTaxesSummary &&
      (isPreviewData || displayData.totalSales || displayData.totalTaxes),
    shouldShowDevices:
      settings.showDevicesSummary &&
      (isPreviewData || displayData.deviceTransactions),
    shouldShowVoided:
      settings.showVoidedItems &&
      (isPreviewData || Boolean(displayData.voidedItems?.length)),
    shouldShowDiscounted:
      settings.showDiscountedItems &&
      (isPreviewData || Boolean(displayData.discountedItems?.length)),
    shouldShowRefunded:
      settings.showRefundedItems &&
      (isPreviewData || Boolean(displayData.refundedItems?.length)),
  };
}

// Sample data for preview when no report is available
export const previewData: BusinessReportData = {
  ordersCreated: 0,
  billsClosed: 0,
  totalSales: "0.00",
  totalTaxes: "0.00",
  salesByCategory: [
    {
      categoryId: "1",
      categoryName: "Food",
      amount: "0.00",
      quantity: 0,
    },
    {
      categoryId: "2",
      categoryName: "Beverages",
      amount: "0.00",
      quantity: 0,
    },
    {
      categoryId: "3",
      categoryName: "Desserts",
      amount: "0.00",
      quantity: 0,
    },
  ],
  deviceTransactions: [
    {
      cashCollected: "0.00",
      cashSales: "0.00",
      cashDeposits: "0.00",
      cashWithdrawals: "0.00",
      difference: "0.00",
      inputByEmployeeId: "EMP001",
    },
  ],
  voidedItems: [
    {
      reason: "Customer Complaint",
      amount: "0.00",
      permissionCode: {
        employeeId: "MGR001",
        employeeName: "John Manager",
      },
    },
    {
      reason: "Wrong Order",
      amount: "0.00",
      permissionCode: {
        employeeId: "MGR002",
        employeeName: "Jane Supervisor",
      },
    },
  ],
  discountedItems: [
    {
      reason: "Senior Discount",
      amount: "0.00",
      permissionCode: {
        employeeId: "MGR001",
        employeeName: "John Manager",
      },
    },
  ],
  refundedItems: [
    {
      reason: "Quality Issue",
      amount: "0.00",
      permissionCode: {
        employeeId: "MGR002",
        employeeName: "Jane Supervisor",
      },
    },
  ],
};
